import {ComponentFactory, ComponentFactoryResolver, Injectable, Injector, Type} from "@angular/core";
import {SuiModalService} from "ng2-semantic-ui";
import {Observable} from "rxjs";
import {DxModalResult} from "../domain/dx-modal-result";
import {DxModalConfig} from "../domain/dx-modal-config";
import {DxInfoModalComponent} from "../ui/components/info/dx-info-modal.component";
import {DxModalOptions} from "../domain/ui-options/dx-modal-options";
import {fromPromise} from "rxjs/internal-compatibility";
import {DX_MODALS_CONFIG, DxModalsConfig} from "../config/dx-modals-config";
import {DxModalButton} from "../domain/ui-options/dx-modal-button";
import {DxModalContext} from "../domain/dx-modal-context";
import {DxWarnModalComponent} from "../ui/components/warn/dx-warn-modal.component";
import {DxErrorModalComponent} from "../ui/components/error/dx-error-modal.component";
import {DxConfirmModalComponent} from "../ui/components/confirm/dx-confirm-modal.component";
import {DxEditModalComponent} from "../ui/components/edit/dx-edit-modal.component";
import {DxModalResultType} from "../domain/dx-modal-result-type";
import {PitActivityVersionsModalComponent} from "../../pit-activities/ui/components/activity-versions-modal/pit-activity-versions-modal.component";


/**
 * Сервис для управления модальными окнами приложения.
 * Инжектируется один раз в руте.
 */
@Injectable()
export class DxModalService {
  /** Конфигурация модуля модальных окон. */
  private readonly _config: DxModalsConfig;
  /** Опции модального окна "Информация". */
  private readonly _infoModalOptions: DxModalOptions;
  /** Опции модального окна "Предупреждение". */
  private readonly _warnModalOptions: DxModalOptions;
  /** Опции модального окна "Ошибка". */
  private readonly _errorModalOptions: DxModalOptions;
  /** Опции модального окна "Подтверждение". */
  private readonly _confirmModalOptions: DxModalOptions;
  /** Опции модального окна для отображения произвольного сожержимого. */
  private readonly _editModalOptions: DxModalOptions;


  constructor(private _injector: Injector,
              private _resolver: ComponentFactoryResolver,
              private _suiModalService: SuiModalService) {
    // TODO: Подумать на досуге как лучше обойти проблему не заданного провайдера.
    // TODO: Пока мой выдумыватель больше не думает :(
    try {
      this._config = this._injector.get(DX_MODALS_CONFIG);
    } catch (e) {
      // Это значить что провайдер не определен.
      // Используем проверки по умолчанию.
    }

    if(this._config) {
      this._infoModalOptions = this._config.infoModalConfig ? this._config.infoModalConfig : DxModalService._buildDefaultInfoOptions();
      this._warnModalOptions = this._config.warnModalConfig ? this._config.warnModalConfig : DxModalService._buildDefaultWarnOptions();
      this._errorModalOptions = this._config.errorModalConfig ? this._config.errorModalConfig : DxModalService._buildDefaultErrorOptions();
      this._confirmModalOptions = this._config.confirmModalConfig ? this._config.confirmModalConfig : DxModalService._buildDefaultConfirmOptions();
      this._editModalOptions = this._config.editModalConfig ? this._config.editModalConfig : DxModalService._buildDefaultEditOptions();
    } else {
      this._infoModalOptions = DxModalService._buildDefaultInfoOptions();
      this._warnModalOptions = DxModalService._buildDefaultWarnOptions();
      this._errorModalOptions = DxModalService._buildDefaultErrorOptions();
      this._confirmModalOptions = DxModalService._buildDefaultConfirmOptions();
      this._editModalOptions = DxModalService._buildDefaultEditOptions();
    }
  }


  get infoModalOptions(): DxModalOptions {
    return this._infoModalOptions;
  }

  get warnModalOptions(): DxModalOptions {
    return this._warnModalOptions;
  }

  get errorModalOptions(): DxModalOptions {
    return this._errorModalOptions;
  }

  get confirmModalOptions(): DxModalOptions {
    return this._confirmModalOptions;
  }

  get editModalOptions(): DxModalOptions {
    return this._editModalOptions;
  }


  /**
   * Показывает информационное модальное окно.
   * @param {string} message - иформационное сообщение.
   */
  public showInfo(message: string): Observable<DxModalResult> {
    return this._showModal(DxInfoModalComponent, this._infoModalOptions, message);
  }


  /**
   * Показывает модальное окно c предупреждением.
   * @param {string} message - иформационное сообщение.
   */
  public showWarn(message: string): Observable<DxModalResult> {
    return this._showModal(DxWarnModalComponent, this._warnModalOptions, message);
  }


  /**
   * Показывает модальное окно c ошибкой.
   * @param {string} message - иформационное сообщение.
   */
  public showError(message: string): Observable<DxModalResult> {
    return this._showModal(DxErrorModalComponent, this._errorModalOptions, message);
  }


  /**
   * Показывает модальное окно c подтверждением.
   * @param {string} message - иформационное сообщение.
   */
  public showConfirm(message: string): Observable<DxModalResult> {
    return this._showModal(DxConfirmModalComponent, this._confirmModalOptions, message);
  }



  /**
   * Показывает модальное окно для редактирования данных.
   *
   * !!! ВНИМАНИЕ!!!
   * Данные метод должен использоваться если
   *
   * @param {clazz: Type<any>} clazz - класс комопонента, который нужно отобразить в контенте модалки.
   *                                   Данный класс должен быть добавлен в entryComponents: [] модуля.
   * @param {Map<string, any>} inputs - мапа аргументов комопнента. Передается как есть. Комопнент разбирает ее сам.
   * @param {string} title - Опционально можно задать заголовок окна отличный от заданных в опциях.
   */
  public showEditByType(clazz: Type<any>, inputs?: any | Map<string, any>, options?: DxModalOptions): Observable<DxModalResult> {
    const context: DxModalContext = new DxModalContext(options ? options : this._editModalOptions);
    context.componentFactory = this._resolver.resolveComponentFactory(clazz);
    context.inputs = inputs;

    const promise = new Promise<DxModalResult>((resolve) => {
      this._suiModalService.open(new DxModalConfig(DxEditModalComponent, context))
        .onApprove((result: DxModalResult) => {
          resolve(result);
        })
        .onDeny((result: DxModalResult) => {
          resolve(result);
        });
    });
    return fromPromise<DxModalResult>(promise);
  }



  /**
   * Показывает модальное окно для редактирования данных.
   * @param {factory: ComponentFactory<any>} factory - фабрика entry-комопонента, созданная при помощи резолвера
   *                                         `ComponentFactoryResolver<any>` в модуле, в котором определен entry-комопонент.
   *
   * Короче в чем соль. Если у нас в системе один рутовый модуль. То есть и модалки и entry-комопоненты определены в руте.
   * То все будет создаваться без проблем. Но если модалки и entry-комопоненты раскиданны по разным (в том числе lazy) модулям,
   * то попытка получить инстанс фабрики копонета внутри модуля с модалками приведет к ошибке, так как судя по всему
   * инстас `ComponentFactoryResolver<T>` относится к конкретному модулю.
   *
   * Тема с получением инстанса провайдера через инжектор прокатит только для root- и eager-модулей, и не прокатит
   * для lazy-модулей, так как они имеют свой инжектор.
   *
   * То есть, мы регистрируем провадер в lazy-модуле:
   * ```
   * providers: [
   *    <...>ModalComponentFactoryProvider,
   *    {
   *       provide: '<...>_MODAL_COMPONENT_FACTORY_PROVIDER',
   *       useClass: PitModalComponentFactoryProvider,
   *       multi: true
   *    }
   * ]
   * ```
   * Но этот провайдер доступен для инжектирования только через инжектор lazy-модуля.
   * А модалки у нас испортированы в руте. Следовательно имееют рутовый инжектор и не знают этот провайдер.
   *
   * То есть нужно спускаться по дереву инжекторов и искать инжектор нужного модуля. Тут опятьже нужно передавать
   * какие-то параметры (имя модуля или провайдера). Хрень короче. Сложно и не прозрачно.
   *
   * Пусть лучше каждый модуль, в котром есть необходимость открытия каких-то компонентов в модальном окне,
   * создает провайдера для фабрик этих комопнентов.
   *
   * С точки зрения списков и гридов тоже все нормально. Там все равно нужно реализовывать обертку - провайдера модалок
   * для списка \ грида. Поэтому создание фабрик для комопнентов можно спрятать в этой обертке.
   *
   * @param inputs - типизированный объект или мапа аргументов комопнента. Передается как есть. Комопнент разбирает ее сам.
   * @param {string} title - Опционально можно задать заголовок окна отличный от заданных в опциях.
   */
  public showEditByFactory(factory: ComponentFactory<any>, inputs?: any | Map<string, any>, title?: string, options?: DxModalOptions): Observable<DxModalResult> {
    let context: DxModalContext;
    if (options) {
      context = new DxModalContext(options);
    } else {
      context = new DxModalContext(this._editModalOptions);
    }
    context.componentFactory = factory;
    context.inputs = inputs;
    if(title) context.title = title;

    const promise = new Promise<DxModalResult>((resolve) => {
      this._suiModalService.open(new DxModalConfig(DxEditModalComponent, context))
        .onApprove((result: DxModalResult) => {
          resolve(result);
        })
        .onDeny((result: DxModalResult) => {
          resolve(result);
        });
    });
    return fromPromise<DxModalResult>(promise);
  }


  public buildEditModalOptions(size: string = 'normal', saveButtonTitle: string = 'Сохранить', saveButtonResultType: DxModalResultType = DxModalResultType.SAVE): DxModalOptions {
    const options: DxModalOptions = new DxModalOptions();

    options.size = size;
    options.closeOnBackgroundClick = true; // не работает
    options.buttons = [];

    let button = new DxModalButton();
    button.title = saveButtonTitle;
    button.resultType = saveButtonResultType;
    button.ord = 1;
    options.buttons.push(button);

    button = new DxModalButton();
    button.title = 'Отмена';
    button.resultType = 'CANCEL';
    button.ord = 2;
    options.buttons.push(button);

    return options;
  }



  /**
   * Внутренний метод для открытия модального окна.
   * @param {clazz: Type<any>} clazz - класс комопнента модального окна.
   * @param {DxModalOptions} options - опции модального окна.
   * @param {string} message - сообщение.
   */
  private _showModal(clazz: Type<any>, options: DxModalOptions, message: string): Observable<DxModalResult> {
    const context: DxModalContext = new DxModalContext(options);
    context.message = message;

    const promise = new Promise<DxModalResult>((resolve) => {
      this._suiModalService.open(new DxModalConfig(clazz, context))
        .onApprove((result: DxModalResult) => {
          resolve(result);
        })
        .onDeny((result: DxModalResult) => {
          resolve(result);
        });
    });
    return fromPromise<DxModalResult>(promise);
  }


  private static _buildDefaultInfoOptions(): DxModalOptions {
    const options: DxModalOptions = new DxModalOptions();
    options.title = 'Информация';
    options.size = 'tiny';
    options.closeOnBackgroundClick = false;
    options.buttons = [];

    let button = new DxModalButton();
    button.title = 'OK';
    button.resultType = 'OK';
    button.ord = 1;
    options.buttons.push(button);

    return options;
  }


  private static _buildDefaultWarnOptions(): DxModalOptions {
    const options: DxModalOptions = new DxModalOptions();
    options.title = 'Предупреждение';
    options.size = 'tiny';
    options.closeOnBackgroundClick = false;
    options.buttons = [];

    let button = new DxModalButton();
    button.title = 'OK';
    button.resultType = 'OK';
    button.ord = 1;
    options.buttons.push(button);

    return options;
  }


  private static _buildDefaultErrorOptions(): DxModalOptions {
    const options: DxModalOptions = new DxModalOptions();
    options.title = 'Ошибка';
    options.size = 'tiny';
    options.closeOnBackgroundClick = false;
    options.buttons = [];

    let button = new DxModalButton();
    button.title = 'OK';
    button.resultType = 'OK';
    button.ord = 1;
    options.buttons.push(button);

    return options;
  }


  private static _buildDefaultConfirmOptions(): DxModalOptions {
    const options: DxModalOptions = new DxModalOptions();
    options.title = 'Подтверждение';
    options.size = 'tiny';
    options.closeOnBackgroundClick = false;
    options.buttons = [];

    let button = new DxModalButton();
    button.title = 'OK';
    button.resultType = 'OK';
    button.ord = 1;
    options.buttons.push(button);

    button = new DxModalButton();
    button.title = 'Отмена';
    button.resultType = 'CANCEL';
    button.ord = 2;
    options.buttons.push(button);

    return options;
  }


  private static _buildDefaultEditOptions(): DxModalOptions {
    const options: DxModalOptions = new DxModalOptions();
    options.title = 'Редактирование';
    options.size = 'small';
    options.closeOnBackgroundClick = false;
    options.buttons = [];

    let button = new DxModalButton();
    button.title = 'Сохранить';
    button.resultType = 'SAVE';
    button.ord = 1;
    button.disabled = true;
    options.buttons.push(button);

    button = new DxModalButton();
    button.title = 'Отмена';
    button.resultType = 'CANCEL';
    button.ord = 2;
    options.buttons.push(button);

    return options;
  }
}
